import ServiceOrders from "@/components/ServiceOrders";
import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Loader2, LogOut, Pencil, Plus, Sparkles, Trash2, X } from "lucide-react";
import Seo from "@/components/Seo";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const EMPTY = { type: "", city: "", district: "", area: "", rooms: "", price: "", description: "" };
const FIELDS = [
  ["type", "نوع العقار", "فيلا حديثة"],
  ["city", "المدينة", "الرياض"],
  ["district", "الحي", "حي الياسمين"],
  ["area", "المساحة (م²)", "450"],
  ["rooms", "عدد الغرف", "5"],
  ["price", "السعر (ر.س)", "3,200,000"],
];

export default function AdminPage() {
  const location = useLocation();
  const [auth, setAuth] = useState(location.state?.user ? true : null);
  const [authError, setAuthError] = useState(!!location.state?.authError);
  const [user, setUser] = useState(location.state?.user || null);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginBusy, setLoginBusy] = useState(false);
  const [loginError, setLoginError] = useState("");

  const loadItems = useCallback(() => {
    fetch(`${API}/properties`)
      .then((r) => r.json())
      .then((d) => setItems(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (location.state?.user) {
      loadItems();
      return;
    }
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((u) => {
        setUser(u);
        setAuth(true);
        loadItems();
      })
      .catch(() => setAuth(false));
  }, [location.state, loadItems]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const login = () => {
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    setAuth(false);
    setUser(null);
  };

  const formatDetail = (d) =>
    typeof d === "string"
      ? d
      : Array.isArray(d)
        ? d.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ")
        : "حدث خطأ، حاول مرة أخرى";

  const passwordLogin = async (e) => {
    e.preventDefault();
    setLoginBusy(true);
    setLoginError("");
    try {
      const res = await fetch(`${API}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(loginForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatDetail(data.detail));
      setUser(data);
      setAuth(true);
      loadItems();
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setLoginBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setNotice("");
    const isNew = !form.ref;
    const res = await fetch(isNew ? `${API}/properties` : `${API}/properties/${form.ref}`, {
      method: isNew ? "POST" : "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        type: form.type,
        city: form.city,
        district: form.district,
        area: form.area,
        rooms: form.rooms,
        price: form.price,
        description: form.description || "",
      }),
    });
    setBusy(false);
    if (res.ok) {
      setForm(null);
      setNotice(isNew ? "تمت إضافة العقار بنجاح" : "تم حفظ التعديلات بنجاح");
      loadItems();
    } else {
      setNotice("فشل الحفظ — حاول مرة أخرى");
    }
  };

  const remove = async (ref) => {
    if (!window.confirm(`حذف العقار ${ref} نهائيًا؟`)) return;
    const res = await fetch(`${API}/properties/${ref}`, { method: "DELETE", credentials: "include" });
    if (res.ok) {
      setNotice("تم حذف العقار");
      loadItems();
    }
  };

  const [generating, setGenerating] = useState(false);

  const generateDesc = async () => {
    setGenerating(true);
    setNotice("");
    try {
      const res = await fetch(`${API}/properties/generate-description`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          type: form.type,
          city: form.city,
          district: form.district,
          area: form.area,
          rooms: form.rooms,
          price: form.price,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "تعذر التوليد — حاول مرة أخرى");
      setForm({ ...form, description: data.description });
    } catch (err) {
      setNotice(err.message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div data-testid="admin-page" className="min-h-screen bg-navy">
      <Seo title="لوحة التحكم | شيادن الأصول العقارية" siteName="شيادن الأصول العقارية" description="لوحة تحكم المشرف." />

      <header className="border-b border-white/10">
        <div className="mx-auto flex h-[72px] max-w-5xl items-center justify-between px-5">
          <Link to="/" data-testid="admin-brand-link" className="leading-none">
            <span className="font-display text-xl font-bold tracking-[0.18em] text-cream">SHIYADN</span>
            <span className="mt-1 block font-head text-[11px] font-semibold text-copper">لوحة التحكم</span>
          </Link>
          {auth === true && (
            <button
              onClick={logout}
              data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-cream/30 px-5 py-2 font-head text-sm font-bold text-cream transition-colors duration-300 hover:border-copper hover:text-copper"
            >
              <LogOut className="h-4 w-4" />
              تسجيل الخروج
            </button>
          )}
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-12 md:py-16">
        {auth === true && <ServiceOrders />}
        {auth === null && (
          <div className="flex justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-copper" />
          </div>
        )}

        {auth === false && (
          <div className="mx-auto max-w-md border border-white/10 bg-white/5 p-10 text-center">
            <span className="font-display text-2xl font-bold tracking-[0.15em] text-cream">SHIYADN</span>
            <h1 className="mt-4 font-head text-2xl font-bold text-cream">منطقة المشرف</h1>
            <p className="mt-3 text-sm leading-relaxed text-cream/60">
              هذه المنطقة مخصصة لإدارة العقارات المعروضة. الدخول بحساب Google المصرح به فقط.
            </p>
            <form onSubmit={passwordLogin} className="mt-8 space-y-3 text-right">
              <input
                type="email"
                required
                placeholder="البريد الإلكتروني"
                value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                data-testid="admin-email-input"
                className="w-full border border-white/15 bg-white/5 px-4 py-3 text-sm text-cream outline-none transition-colors placeholder:text-cream/30 focus:border-copper"
              />
              <input
                type="password"
                required
                placeholder="كلمة المرور"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                data-testid="admin-password-input"
                className="w-full border border-white/15 bg-white/5 px-4 py-3 text-sm text-cream outline-none transition-colors placeholder:text-cream/30 focus:border-copper"
              />
              {(loginError || authError) && (
                <p data-testid="admin-auth-error" className="border border-red-400/40 bg-red-400/10 p-3 text-sm text-red-300">
                  {loginError || "تعذر تسجيل الدخول — هذا الحساب غير مصرح له."}
                </p>
              )}
              <button
                type="submit"
                disabled={loginBusy}
                data-testid="admin-password-login-button"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-copper py-3.5 font-head text-sm font-bold text-navy transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
              >
                {loginBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                دخول
              </button>
            </form>

            <div className="my-6 flex items-center gap-3 text-xs text-cream/30">
              <span className="h-px flex-1 bg-white/10" />
              أو
              <span className="h-px flex-1 bg-white/10" />
            </div>

            <button
              onClick={login}
              data-testid="admin-login-button"
              className="inline-flex w-full items-center justify-center gap-3 rounded-full border border-cream/25 py-3.5 font-head text-sm font-bold text-cream transition-colors duration-300 hover:border-copper hover:text-copper"
            >
              تسجيل الدخول بحساب Google
            </button>
            <Link to="/" className="mt-5 inline-flex items-center gap-2 text-xs text-cream/50 transition-colors hover:text-copper">
              <ArrowRight className="h-3.5 w-3.5" />
              العودة للموقع
            </Link>
          </div>
        )}

        {auth === true && (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <h1 className="font-head text-3xl font-extrabold text-cream">العقارات الأسبوعية</h1>
                <p className="mt-1 text-sm text-cream/50">
                  مرحبًا {user?.name || user?.email} — التعديلات تظهر على الموقع فور الحفظ
                </p>
              </div>
              <button
                onClick={() => setForm({ ...EMPTY })}
                data-testid="admin-add-property-button"
                className="inline-flex items-center gap-2 rounded-full bg-copper px-6 py-3 font-head text-sm font-bold text-navy transition-transform duration-300 hover:scale-105"
              >
                <Plus className="h-4 w-4" />
                إضافة عقار
              </button>
            </div>

            {notice && (
              <p data-testid="admin-notice" className="mt-6 border border-copper/40 bg-copper/10 p-3 text-sm text-copper">
                {notice}
              </p>
            )}

            <div className="mt-8 space-y-4">
              {items.map((p) => (
                <div
                  key={p.ref}
                  data-testid={`property-row-${p.ref}`}
                  className="flex flex-wrap items-center justify-between gap-4 border border-white/10 bg-white/5 p-5"
                >
                  <div className="flex items-center gap-5">
                    <span className="font-display text-lg font-semibold tracking-[0.2em] text-copper">{p.ref}</span>
                    <div>
                      <span className="block font-head text-base font-bold text-cream">{p.type}</span>
                      <span className="mt-0.5 block text-xs text-cream/50">
                        {p.city} · {p.district} · {p.area} م² · {p.rooms} غرف · {p.price} ر.س
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setForm({ ...p })}
                      data-testid={`property-edit-${p.ref}`}
                      aria-label="تعديل"
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-cream/20 text-cream/70 transition-colors hover:border-copper hover:text-copper"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => remove(p.ref)}
                      data-testid={`property-delete-${p.ref}`}
                      aria-label="حذف"
                      className="flex h-10 w-10 items-center justify-center rounded-full border border-cream/20 text-cream/70 transition-colors hover:border-red-400 hover:text-red-400"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
              {items.length === 0 && (
                <p className="border border-dashed border-white/20 p-8 text-center text-sm text-cream/50">
                  لا توجد عقارات حاليًا — أضف أول عقار
                </p>
              )}
            </div>
          </>
        )}
      </main>

      {form && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-navy/80 backdrop-blur-sm md:items-center" onClick={() => setForm(null)}>
          <div
            data-testid="property-form"
            className="w-full max-w-lg border border-white/10 bg-deepnavy p-6 md:p-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h2 className="font-head text-xl font-bold text-cream">
                {form.ref ? `تعديل العقار ${form.ref}` : "إضافة عقار جديد"}
              </h2>
              <button onClick={() => setForm(null)} data-testid="property-form-close" aria-label="إغلاق" className="text-cream/60 hover:text-copper">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mt-6 grid grid-cols-2 gap-4">
              {FIELDS.map(([key, label, placeholder]) => (
                <label key={key} className="block">
                  <span className="mb-1.5 block text-xs font-semibold text-cream/60">{label}</span>
                  <input
                    value={form[key]}
                    placeholder={placeholder}
                    data-testid={`property-field-${key}`}
                    onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                    className="w-full border border-white/15 bg-white/5 px-3.5 py-2.5 text-sm text-cream outline-none transition-colors placeholder:text-cream/25 focus:border-copper"
                  />
                </label>
              ))}
            </div>
            <div className="mt-5">
              <div className="mb-1.5 flex items-center justify-between gap-3">
                <span className="text-xs font-semibold text-cream/60">وصف العقار (يظهر في الموقع)</span>
                <button
                  type="button"
                  onClick={generateDesc}
                  disabled={generating}
                  data-testid="generate-description-button"
                  className="inline-flex items-center gap-1.5 rounded-full border border-copper/50 px-3.5 py-1.5 text-xs font-bold text-copper transition-colors duration-300 hover:bg-copper hover:text-navy disabled:opacity-50"
                >
                  {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  ولّد بالذكاء الاصطناعي
                </button>
              </div>
              <textarea
                rows={3}
                value={form.description || ""}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="property-field-description"
                placeholder="وصف تسويقي قصير للعقار — ولّده بالذكاء الاصطناعي أو اكتبه بنفسك"
                className="w-full resize-none border border-white/15 bg-white/5 px-3.5 py-2.5 text-sm leading-relaxed text-cream outline-none transition-colors placeholder:text-cream/25 focus:border-copper"
              />
            </div>
            <button
              onClick={save}
              disabled={busy}
              data-testid="property-save-button"
              className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-copper py-3.5 font-head text-sm font-bold text-navy transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {form.ref ? "حفظ التعديلات" : "إضافة العقار"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
